import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { InputFieldComponent } from '../../../../shared/components/ui/input-field/input-field.component';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { UserService } from '../../../user/service/user.service';
import { DutyTemplateService } from '../../services/duty-template.service';
import {
  DUTY_RECURRENCE_OPTIONS,
  type DutyRecurrenceFrequency,
} from '../../models/duty-recurrence.model';
import type {
  DutyTemplate,
  DutyTemplateItem,
} from '../../models/duty-template.model';

export type RoutineBuilderMode = 'create' | 'edit';

export interface RoutineBuilderDialogData {
  mode: RoutineBuilderMode;
  template?: DutyTemplate;
  prefill?: {
    name: string;
    items: DutyTemplateItem[];
  };
}

const DEFAULT_REWARD_XP = 10;
const MAX_ITEMS = 20;

@Component({
  selector: 'app-routine-builder-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    TranslatePipe,
    InputFieldComponent,
    ParentButtonComponent,
  ],
  templateUrl: './routine-builder-dialog.component.html',
  styleUrl: './routine-builder-dialog.component.scss',
})
export class RoutineBuilderDialogComponent implements OnInit {
  private readonly dialogRef = inject(
    MatDialogRef<RoutineBuilderDialogComponent>,
  );
  private readonly dialogData = inject(MAT_DIALOG_DATA, {
    optional: true,
  }) as RoutineBuilderDialogData | undefined;
  private readonly fb = inject(FormBuilder);
  #templates = inject(DutyTemplateService);
  #userService = inject(UserService);

  readonly recurrenceOptions = DUTY_RECURRENCE_OPTIONS.filter(
    (o) => o.id !== 'NONE',
  );
  readonly isEditMode = computed(
    () => this.dialogData?.mode === 'edit' && !!this.dialogData?.template,
  );
  readonly experiencePoolBalance = computed(
    () => this.#userService.experiencePoolBalance(),
  );
  readonly isPoolLow = computed(() => {
    const balance = this.experiencePoolBalance();
    return balance != null && balance < 50;
  });

  isSubmitting = signal(false);
  nameValidation = signal<Record<string, string>>({
    required: '',
    minlength: '',
  });
  stepTitleValidation = signal<Record<string, string>>({
    required: '',
    minlength: '',
  });
  stepContentValidation = signal<Record<string, string>>({
    required: '',
    minlength: '',
  });

  routineForm!: FormGroup;

  ngOnInit(): void {
    this.routineForm = this.fb.group({
      name: [
        this.dialogData?.template?.name ??
          this.dialogData?.prefill?.name ??
          '',
        [Validators.required, Validators.minLength(3)],
      ],
      items: this.fb.array(
        this.initialItems().map((item) => this.buildItemGroup(item)),
      ),
    });

    if (this.items.length === 0) {
      this.addStep();
    }
  }

  get items(): FormArray {
    return this.routineForm.get('items') as FormArray;
  }

  itemGroups(): FormGroup[] {
    return this.items.controls as FormGroup[];
  }

  recurrenceForIndex(index: number): DutyRecurrenceFrequency {
    const value = this.items.at(index)?.get('recurrenceFrequency')?.value;
    return (value as DutyRecurrenceFrequency) || 'DAILY';
  }

  canConfirm(): boolean {
    return this.routineForm.valid && this.items.length > 0 && !this.isSubmitting();
  }

  addStep(): void {
    if (this.items.length >= MAX_ITEMS) return;
    this.items.push(this.buildItemGroup());
  }

  removeStep(index: number): void {
    if (this.items.length <= 1) return;
    this.items.removeAt(index);
  }

  selectRecurrence(index: number, frequency: DutyRecurrenceFrequency): void {
    this.items.at(index)?.patchValue({ recurrenceFrequency: frequency });
  }

  onDiscard(): void {
    this.dialogRef.close();
  }

  async onConfirm(): Promise<void> {
    if (!this.canConfirm()) return;

    this.isSubmitting.set(true);
    const name = String(this.routineForm.value.name ?? '').trim();
    const items = this.items.getRawValue().map(
      (raw: {
        title: string;
        content: string;
        rewardXp: number | string;
        rewardGold: number | string;
        recurrenceFrequency: DutyRecurrenceFrequency;
      }) => ({
        title: String(raw.title ?? '').trim(),
        content: String(raw.content ?? '').trim(),
        rewardXp: this.resolveRewardXp(raw.rewardXp),
        rewardGold: this.resolveRewardGold(raw.rewardGold),
        recurrenceFrequency: raw.recurrenceFrequency || 'DAILY',
      }),
    ) satisfies DutyTemplateItem[];

    try {
      if (this.isEditMode() && this.dialogData?.template?.id) {
        await this.#templates.updateTemplate.mutateAsync({
          templateId: this.dialogData.template.id,
          name,
          items,
        });
      } else {
        await this.#templates.createTemplate.mutateAsync({ name, items });
      }
      this.dialogRef.close(true);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private initialItems(): DutyTemplateItem[] {
    const templateItems = this.dialogData?.template?.items ?? [];
    if (templateItems.length > 0) {
      return templateItems;
    }
    const prefillItems = this.dialogData?.prefill?.items ?? [];
    if (prefillItems.length > 0) {
      return prefillItems;
    }
    return [];
  }

  private buildItemGroup(item?: DutyTemplateItem): FormGroup {
    return this.fb.group({
      title: [item?.title ?? '', [Validators.required, Validators.minLength(3)]],
      content: [
        item?.content ?? '',
        [Validators.required, Validators.minLength(10)],
      ],
      rewardXp: [item?.rewardXp ?? DEFAULT_REWARD_XP],
      rewardGold: [item?.rewardGold ?? 0],
      recurrenceFrequency: [
        (item?.recurrenceFrequency as DutyRecurrenceFrequency) || 'DAILY',
      ],
    });
  }

  private resolveRewardXp(value: number | string | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_REWARD_XP;
  }

  private resolveRewardGold(value: number | string | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }
}
